import { BREAKPOINTS } from "../../config/breakpoints";
/* eslint-disable no-redeclare */
import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import Button from "../../Generic/Button";
import Card from "../../Generic/Card";
import InputBox from "../../Generic/InputBox";
import SuccessButton from "../../Generic/SuccessButton";
import { successNotification, errorNotification } from "../../Generic/Notifications";
import { RecipeSegmentsEdit } from "./RecipeSegmentsEdit";
import close from "../../Assets/Close.png";
import { CSSTransition } from "react-transition-group";
import { useNavigate, useParams } from "react-router-dom";
import StudioService from "../Services/StudioService";
import { useRecipe } from "./context/RecipeContext";
import { Patterns } from "./constants";
import { zIndex } from "../../config/zIndex";

const Page = styled.div`
	height: 100%;
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 1rem;
`;

const Div = styled.div`
	height: ${(props) => props.height};
	overflow-y: ${(props) => props.overflowY};
	background: ${(props) => props.background};
	color: ${(props) => props.color};
	position: ${(props) => props.position};
	width: ${(props) => props.width};
	display: flex;
	flex-direction: ${(props) => props.flexDirection};
	flex-wrap: ${(props) => props.flexWrap};
	justify-content: ${(props) => props.jContent};
	align-items: ${(props) => props.alignItem};
	padding: ${(props) => props.padding};
	margin: ${(props) => props.margin};
	text-align: ${(props) => props.txtAlign};
	font-size: ${(props) => props.fontSize};
	font-weight: ${(props) => props.fontWeight};
	font-family: ${(props) => props.fontFamily};
	gap ${(props) => props.gap};
	@media (max-width: ${BREAKPOINTS.desktop}px) {
		justify-content: ${(props) => props.jContentBefore1000};
	}
`;

const Span = styled.span`
	font-size: ${(props) => props.fontSize};
	font-weight: ${(props) => props.fontWeight};
	font-family: ${(props) => props.fontFamily};
	color: ${(props) => props.color};
	margin: ${(props) => props.margin};
	padding: ${(props) => props.padding};
`;

// Reusable styled components
const Column = styled(Div)`
	flex-direction: column;
`;

const FieldColumn = styled(Column)`
	/* Column container for a label + input */
`;

const Label = styled(Span)`
	color: rgba(0, 0, 0, 0.6);
`;

const RowWrap = styled(Div)`
	flex-wrap: wrap;
	gap: 1rem;
`;

const ActionRow = styled(Div)`
	justify-content: flex-end;
	gap: 1rem;
	padding: 1rem 0;
`;

// Input wrapper components with standardized props
const DefaultInput = (props) => {
	const { width, breakpoint, widthBeforeBreakpoint, ...rest } = props;
	return <InputBox padding="0.5rem" margin="0.5rem 0 0 0" width={"20rem"} {...rest} />;
};

const Overlay = styled.div`
	position: fixed;
	display: flex;
	justify-content: center;
	align-items: center;
	background: rgba(0, 0, 0, 0.3);
	left: 0;
	top: 0;
	right: 0;
	bottom: 0;
	z-index: ${zIndex.modalBackdrop};
`;

const Modal = styled.div`
	display: flex;
	flex-direction: column;
	background: #f2f2f2;
	max-height: 90%;
	height: max-content;
	width: 60vw;
	overflow-x: hidden;
	border-radius: 10px;
	z-index: ${zIndex.modal};
`;

const RecipeEdit = () => {
	const { editingRecipe, updateRecipe, changeEditMode } = useRecipe();
	const [confirmModal, showConfirmModal] = useState(false);
	const segmentsRef = useRef(editingRecipe?.segments);
	const navigate = useNavigate();
	const { id } = useParams();

	function updateData(event) {
		//Get numeric input for fields if type is set as number

		let payload = {};
		switch (event.target.type) {
			case "number":
				payload = { column: event.target.id, value: event.target.valueAsNumber };
				break;
			case "checkbox":
				payload = { column: event.target.id, value: event.target.checked };
				break;
			default:
				payload = { column: event.target.id, value: event.target.value };
				break;
		}
		updateRecipe(payload);
	}

	async function sendUpdatedData() {
		const payload = { ...editingRecipe, segments: segmentsRef.current ?? editingRecipe?.segments };
		const response = await StudioService.updateRecipe(id, payload);
		const { data, error } = response;
		if (data) {
			showConfirmModal(false);
			successNotification("Changes saved successfully");
			navigate(`/studio/recipe/${id}`);
		}
		if (error) {
			errorNotification(error);
		}
	}

	function renderRecipeEdit(recipe) {
		return (
			<>
				<Card margin="1rem 0 0 0" width="100%" alignItem="center" flexDirection="column">
					<RowWrap flexDirection="row" width="100%" alignItem="center">
						<FieldColumn width="100%">
							<Label>Name</Label>
							<DefaultInput
								id={"name"}
								onChange={(e) => {
									updateData(e);
								}}
								defaultValue={recipe?.name}
								width="100%"
							/>
						</FieldColumn>
						<FieldColumn width="100%">
							<Label>Description</Label>
							<DefaultInput
								id={"description"}
								onChange={(e) => {
									updateData(e);
								}}
								defaultValue={recipe?.description}
								width="100%"
							/>
						</FieldColumn>
						<FieldColumn width="100%">
							<Label>Note</Label>
							<DefaultInput
								id={"note"}
								onChange={(e) => {
									updateData(e);
								}}
								defaultValue={recipe?.note}
								width="100%"
							/>
						</FieldColumn>
					</RowWrap>
				</Card>
				<RecipeSegmentsEdit
					segmentsRef={segmentsRef}
					initialSegments={recipe?.segments}
					segmentsSyncKey={id}
					needMixing={recipe?.needMixing}
					Patterns={Patterns}
					updatePayload={updateRecipe}
				/>
				<ActionRow>
					<Div>
						<SuccessButton
							onClick={() => {
								sendUpdatedData();
							}}>
							Confirm
						</SuccessButton>
					</Div>
				</ActionRow>
			</>
		);
	}

	useEffect(() => {
		return () => {
			changeEditMode({ editMode: false, objectToEdit: {} })
		}
	}, [changeEditMode]);

	return (
		<Page>
			<Div padding="0 0 1rem 0" width="100%" jContent="space-between" alignItem="center">
				<span
					onClick={() => navigate(`/studio/recipe/${id}?action=noReload`)}
					className="material-symbols-outlined"
					style={{ cursor: "pointer" }}>
					arrow_back
				</span>
			</Div>
			{editingRecipe && renderRecipeEdit(editingRecipe)}
			<CSSTransition in={confirmModal} timeout={1000} classNames="container" unmountOnExit>
				<Overlay>
					<Modal>
						<Div
							jContent="space-between"
							alignItem="center"
							color="#fff"
							width="100%"
							background="rgba(45, 35, 66, 0.8)"
							fontSize="1.7rem"
							padding="1rem">
							<Span>Save Changes</Span>
							<img
								onClick={() => {
									showConfirmModal(false);
								}}
								src={close}
								alt="close"
								style={{
									height: "30px",
									filter: "invert(97%) sepia(0%) saturate(7500%) hue-rotate(126deg) brightness(111%) contrast(100%)",
								}}
							/>
						</Div>
						{/* <Div overflowY="auto" flexDirection="column" padding="0rem 1rem 1rem 1rem">
							{renderChanges()}
						</Div> */}

						<ActionRow>
							<Div
								onClick={() => {
									showConfirmModal(false);
								}}>
								<Button>Cancel</Button>
							</Div>
							<Div>
								<SuccessButton
									onClick={() => {
										sendUpdatedData();
									}}>
									Confirm
								</SuccessButton>
							</Div>
						</ActionRow>
					</Modal>
				</Overlay>
			</CSSTransition>
		</Page>
	);
};

export default RecipeEdit;
